/**
 * Change Password Page
 *
 * Forced-change flow for users on an admin-issued temporary password.
 * Reached automatically when the backend returns 403 PASSWORD_CHANGE_REQUIRED
 * (see http-client.ts interceptor) — every other route is gated until the
 * user completes this form.
 *
 * The page is deliberately standalone (no Layout / Sidebar / Header) so a
 * user who still has a pending flag can't navigate away without setting a
 * real password.
 */

import { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { httpClient } from '../../api/http-client';

/**
 * Password input with an inline show/hide eye toggle. Tiny helper so the
 * same shape (label + input + toggle) can be repeated 3x on the form
 * without copy-pasted JSX drifting.
 */
interface PasswordInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  helperText?: string;
  minLength?: number;
}

function PasswordInput({
  id,
  label,
  value,
  onChange,
  autoComplete,
  helperText,
  minLength,
}: PasswordInputProps) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          required
          minLength={minLength}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {helperText && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
    </div>
  );
}

export function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (): string | null => {
    if (!currentPassword) return 'Current password is required.';
    if (!newPassword) return 'New password is required.';
    if (newPassword.length < 10)
      return 'New password must be at least 10 characters.';
    if (newPassword === currentPassword)
      return 'New password must differ from the current (temporary) password.';
    if (newPassword !== confirmPassword)
      return 'Passwords do not match.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await httpClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Reload the root path so the auth state refreshes and the (now-cleared)
      // password_change_required flag is reflected in the role cache.
      window.location.href = '/dashboard';
    } catch (err: any) {
      const serverMsg =
        err?.details?.error || err?.message || 'Failed to change password.';
      setError(serverMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Set your password
          </h1>
          <p className="text-sm text-gray-500">
            Your account was created with a temporary password. Choose a new
            password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordInput
            id="current_password"
            label="Current (temporary) password"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
          />

          <PasswordInput
            id="new_password"
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            minLength={10}
            helperText="At least 10 characters. Avoid reusing the temp password."
          />

          <PasswordInput
            id="confirm_password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            minLength={10}
          />

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 font-medium"
          >
            {isSubmitting ? 'Saving...' : 'Set password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordPage;
